import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default amenities if none are loaded from database
const defaultAmenities = [
  { name: "Fully Equipped Kitchen", icon: "UtensilsCrossed" },
  { name: "Cozy Fireplace", icon: "Flame" },
  { name: "Private Dock", icon: "Anchor" },
  { name: "WiFi Available", icon: "Wifi" },
  { name: "BBQ Grill", icon: "Beef" },
  { name: "Outdoor Fire Pit", icon: "Tent" },
  { name: "Sleeps 6 Guests", icon: "Users" },
  { name: "Pet Friendly", icon: "PawPrint" }
];

const AboutSection = () => {
  const [amenities, setAmenities] = useState([]);
  const [settings, setSettings] = useState({
    about_title: "Your Riverfront Retreat",
    about_description: "Nestled on the serene banks of the Winnipeg River, Stay Tran-Quil offers the perfect escape from the everyday. Our cozy cabin sits on 1.5 acres of private riverfront land, providing an intimate connection with nature while ensuring all the comforts of home."
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [amenitiesRes, settingsRes] = await Promise.all([
          axios.get(`${API}/amenities`),
          axios.get(`${API}/settings/public`)
        ]);
        
        // Use fetched amenities or default if empty
        setAmenities(amenitiesRes.data.length > 0 ? amenitiesRes.data : defaultAmenities);
        
        setSettings(prev => ({
          ...prev,
          about_title: settingsRes.data.about_title || prev.about_title,
          about_description: settingsRes.data.about_description || prev.about_description
        }));
      } catch (err) {
        console.error('Error fetching about data:', err);
        setAmenities(defaultAmenities);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  return (
    <section id="about" className="py-24 bg-stone-900">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-emerald-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
            Welcome to
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {settings.about_title}
          </h2>
          <div className="w-24 h-1 bg-emerald-500 mx-auto mb-8 rounded-full" />
          <p className="text-stone-300 text-lg leading-relaxed">
            {settings.about_description}
          </p>
        </div>

        {/* Amenities Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {amenities.map((amenity, index) => (
            <div
              key={amenity.amenity_id || index}
              className="group bg-stone-800/50 hover:bg-stone-800 border border-stone-700/50 hover:border-emerald-500/30 rounded-2xl p-6 text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-emerald-500/10"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="w-14 h-14 mx-auto mb-4 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-400 group-hover:bg-emerald-500/20 group-hover:scale-110 transition-all duration-300">
                {getIcon(amenity.icon)}
              </div>
              <h3 className="text-white font-medium text-sm">
                {amenity.name}
              </h3>
            </div>
          ))}
        </div>

        {/* Additional Info Card */}
        <div className="mt-16 bg-gradient-to-br from-stone-800/80 to-stone-800/40 border border-stone-700/50 rounded-3xl p-8 md:p-12">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-emerald-400 mb-2">1.5</div>
              <div className="text-stone-400">Acres of Private Land</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-emerald-400 mb-2">6</div>
              <div className="text-stone-400">Guests Maximum</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-emerald-400 mb-2">365</div>
              <div className="text-stone-400">Days of Adventure</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
